import React from "react";
import { Link } from "react-router-dom";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/config";

export default function TermsPage() {
  return (
    <LegalLayout title="Terms of Use" updated="June 24, 2026" testid="terms-page">
      <p>
        Welcome to Not By Will. These Terms of Use (“Terms”) are an agreement between you and Not By Will (“we,”
        “us,” “our”) that governs your use of our website and app at notbywill.com. By creating an account or
        using the app, you agree to these Terms. Please read them alongside our{" "}
        <Link to="/privacy" className="text-brand-teal-dark font-medium hover:underline">Privacy Policy</Link>.
      </p>

      <LegalSection heading="1. What Not By Will is — and isn’t">
        <p>
          Not By Will is a faith-based companion offering support, education, and encouragement for people
          choosing to quit nicotine. <strong>It is not medical care.</strong> We do not provide medical advice,
          diagnosis, treatment, therapy, or counseling, and we are not a substitute for a doctor or licensed
          professional. Nicotine withdrawal is uncomfortable but not usually dangerous; if you have a medical
          condition or concerns, talk with your doctor. <strong>In an emergency, call 911</strong> (or your local
          emergency number).
        </p>
      </LegalSection>

      <LegalSection heading="2. Eligibility">
        <p>
          You must be at least 18 years old (or the age of majority where you live) to use Not By Will. By using
          the app, you confirm that you meet this requirement.
        </p>
      </LegalSection>

      <LegalSection heading="3. Your account">
        <p>
          You’re responsible for the accuracy of your account information, for keeping your password secure, and
          for activity that happens under your account. Please let us know right away if you suspect unauthorized
          use. You can sign in with email and password or with Google.
        </p>
      </LegalSection>

      <LegalSection heading="4. Acceptable use">
        <p>When using Not By Will, you agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Use the app for anything unlawful, harmful, or abusive.</li>
          <li>Harass, demean, or shame other members, including in the Prayer Wall.</li>
          <li>Post content that is hateful, explicit, or that violates others’ rights or privacy.</li>
          <li>Attempt to disrupt, reverse-engineer, or gain unauthorized access to the app.</li>
        </ul>
        <p>We may remove content or suspend accounts that violate these Terms, in order to keep the space safe.</p>
      </LegalSection>

      <LegalSection heading="5. Membership, sponsored access, and giving">
        <p>
          The core of Not By Will — your plan, honest logging, and the Break the Chain craving tool — is free.
          Some deeper features may be offered as member features. Where sponsored access is available, it exists so
          that cost is never the reason someone can’t get help. Any donations or gifts you choose to make support
          the ministry and are voluntary contributions, not purchases; they are generally non-refundable, and we
          do not make any tax-deductibility claims.
        </p>
      </LegalSection>

      <LegalSection heading="6. Community and the Prayer Wall">
        <p>
          The Prayer Wall lets members request and offer prayer with kindness and respect. Share only what you’re
          comfortable making visible to others, and never include sensitive personal information. You’re
          responsible for what you post, and we may remove content or requests at our discretion.
        </p>
      </LegalSection>

      <LegalSection heading="7. Intellectual property">
        <p>
          The app, its content, design, and the Not By Will name and marks belong to us or our licensors. You may
          use the app for your personal recovery journey, but you may not copy, resell, or redistribute it without
          our permission. Scripture quotations are used for encouragement and remain the property of their
          respective publishers.
        </p>
      </LegalSection>

      <LegalSection heading="8. Third-party services">
        <p>
          Not By Will relies on third-party services to work — including Google sign-in, an AI provider for the
          Companion, product analytics, and email delivery. Your use of those features may also be subject to
          those providers’ terms.
        </p>
      </LegalSection>

      <LegalSection heading="9. Disclaimers">
        <p>
          The app is provided “as is” and “as available,” without warranties of any kind, whether express or
          implied. We do not guarantee any particular outcome or result from using Not By Will. Recovery is
          personal, and progress varies from person to person.
        </p>
      </LegalSection>

      <LegalSection heading="10. Limitation of liability">
        <p>
          To the fullest extent permitted by law, Not By Will and its team will not be liable for any indirect,
          incidental, or consequential damages arising from your use of the app. Nothing in these Terms limits
          liability that cannot be limited under applicable law.
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to the service and these Terms">
        <p>
          We may update, add, or remove features over time, and we may revise these Terms. When we make
          significant changes, we’ll update the “Last updated” date above and provide notice in the app. Continued
          use after changes means you accept the updated Terms.
        </p>
      </LegalSection>

      <LegalSection heading="12. Governing law">
        <p>
          These Terms are governed by the laws applicable in the jurisdiction where Not By Will operates, without
          regard to conflict-of-law principles.
        </p>
      </LegalSection>

      <LegalSection heading="13. Contact us">
        <p>
          Questions about these Terms? Email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-teal-dark font-medium hover:underline">{CONTACT_EMAIL}</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
